import React, {useCallback, useContext, useEffect, useState} from "react"
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Table from "@amzn/meridian/table"
import {TableCell, TableRow} from "@amzn/meridian/table"
import Input from "@amzn/meridian/input";
import Pagination from "@amzn/meridian/pagination";
import Box from "@amzn/meridian/box";
import {FormattedMessage} from "react-intl";
import Heading from "@amzn/meridian/heading"
import Loader from "../../loader/Loader";
import {ASCENDING} from "../../util/Constants";
import {BAG_TRACKING_CONSTANTS} from "../../util/Constants";
import {getRFIDStatusRefreshInterval} from "../../util/Util";
import {BagTrackingContext} from "./context/BagTrackingContext";
import {ScrollableElementWrapper, setBagStatus, StageStatusView} from "./BagTrackingUtil";
import {filterBagInfoByDepartTime, filterBagInfo, convertTimestampToLocaleString} from "./BagTrackingUtil";


const BagTracking = () => {
    const {viewModel, dispatch} = useContext(BagTrackingContext);
    const getBagInfo = () => {
        dispatch({type: 'LOAD_VIEW', data:{dispatcher: dispatch}})
    };

    /** In Line Table Filters **/
    const [transporterIdFilter, setTransporterIdFilter] = useState("");
    const [bagIdFilter, setBagIdFilter] = useState("");
    const [bagStatusFilter, setBagStatusFilter] = useState("");

    /** Time and Date Filter **/
    const today = new Date();
    const utcHours = today.getUTCHours();
    const startTime= "12:00:00"; //Hardcoding start time for pilot at DJR5. It is according to depart time at the station.

    let startDate;
    //If current UTC hours is between 0 and 12 then we need to consider previous day also for displaying bagInfo as we're displaying data from 12 UTC (d-1) - 12 UTC (d)
    if (utcHours >= 0 && utcHours <12) {
        startDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 1)).toISOString().slice(0, 10);
    } else {
        startDate = new Date(today).toISOString().slice(0, 10);
    }

    /** Initial Date Time Filter Values **/
    const dateTimeFilterValues = {
        startDate: startDate,
        startTime: startTime,
    };

    /** Sorting. This section keeps the states for sorting column **/
    const [sortColumn, setSortColumn] = useState("departEventTime");
    const [sortDirection, setSortDirection] = useState("ascending");
    const onSort = useCallback(({sortColumn, sortDirection}) => {
        setSortDirection(sortDirection)
        setSortColumn(sortColumn)
    }, []);

    /** Pagination State. This section creates state for pagination. If number of item is greater than 50, a new page will be generated **/
    const [currentPage, setCurrentPage] = useState(1);
    const itemOnPage = 50;

    let bagInfoList = [];
    const bagTrackingMetrics = {
        totalBags: 0,
        departedBags: 0,
        receivedBags: 0,
    };

    if (viewModel.loadingOverview === false) {
        if (viewModel.bagList != null) {
            bagInfoList = viewModel.bagList.bagLocationInfoList ? viewModel.bagList.bagLocationInfoList: [];
            /** Filter Bag Info based on filter values **/
            bagInfoList = bagInfoList
                .filter(bag => filterBagInfoByDepartTime(bag, dateTimeFilterValues))
                .filter(bag => filterBagInfo(bag, transporterIdFilter, "transporterId"))
                .filter(bag => filterBagInfo(bag, bagIdFilter, "bagId"))
                .filter(bag => filterBagInfo(bag, bagStatusFilter, "eventType"));
        };

        bagInfoList.forEach(bag =>{
            setBagStatus(bag);
            switch (bag.eventType) {
                case "Outside Station":
                    bagTrackingMetrics.departedBags = bagTrackingMetrics.departedBags + 1;
                    break;
                case "Inside Station":
                    bagTrackingMetrics.receivedBags = bagTrackingMetrics.receivedBags + 1;
                    break;
                default:
                    break;
            };
            bagTrackingMetrics.totalBags = bagTrackingMetrics.totalBags + 1;
        });
    };

    bagInfoList.sort((a, b) => {
        if (a[sortColumn] < b[sortColumn]) {
            return sortDirection === ASCENDING ? -1 : 1
        };
        if (a[sortColumn] > b[sortColumn]) {
            return sortDirection === ASCENDING ? 1 : -1
        };
        return 0
    });

    /** Pagination State. This section creates state for pagination. If number of item is greater than 50, a new page will be generated **/
    const totalPages = Math.ceil(bagInfoList.length / itemOnPage);
    const startIndexInPage = (currentPage  - 1) * itemOnPage;
    const endIndexInPage = Math.min(startIndexInPage + itemOnPage - 1, bagInfoList.length - 1);
    if (startIndexInPage > bagInfoList.length) {
        setCurrentPage(1);
    };

    /** Get Bag Info and refresh the information in a proper interval**/
    useEffect(() => {
        getBagInfo();
        const interval = setInterval(() => {
            if (!document.hidden) {
                getBagInfo();
            };
        }, getRFIDStatusRefreshInterval());
        return () => {
            clearInterval(interval);
        }
    }, [])

    return (
        viewModel.loadingOverview ?
            <Loader></Loader> :
            <div>
                <Heading level={2}>
                    <FormattedMessage id="bagTracking.heading" defaultMessage="Bag Tracking"/>
                </Heading>
                <div>
                    <Row widths={["grid-4", "grid-4", "grid-4"]} alignmentVertical="bottom" spacingInset="300">
                        <Column>
                            <Row>
                                <Box type="outline" spacingInset="medium">
                                    <StageStatusView stageStatus={{
                                        title: "bag.total",
                                        message: "Total Bags",
                                        value: bagTrackingMetrics.totalBags
                                    }}/>
                                </Box>
                                <Box type="outline" spacingInset="medium">
                                    <Row width="100%" spacing="small" alignmentVertical="bottom">
                                        <StageStatusView
                                            stageStatus={{
                                                title: "bag.departed",
                                                message: "Outside Station",
                                                value: bagTrackingMetrics.departedBags
                                            }}/>
                                        <StageStatusView
                                            stageStatus={{
                                                title: "bag.received",
                                                message: "Inside Station",
                                                value: bagTrackingMetrics.receivedBags
                                            }}/>
                                    </Row>
                                </Box>
                            </Row>
                        </Column>
                        <Column></Column>
                    </Row>

                    <ScrollableElementWrapper>
                        <Row width="100%" widths="fill" className="enable-scroll-x">
                            <div>
                                <div>
                                    <Table showStripes={true} headerColumns={1} stickyHeaderColumn={true}
                                           fixHeaderRows={true} headerRows={1} stickyHeaderRow={true}
                                           showDividers={true} spacing="small"
                                           sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort}>
                                        <TableRow>
                                            < TableCell alignmentHorizontal="center" sortColumn="transporterId">
                                                <FormattedMessage id="bagTable.transporterId"
                                                                  defaultMessage="Transporter ID"/>
                                            </TableCell>
                                            < TableCell alignmentHorizontal="center" sortColumn="bagId">
                                                <FormattedMessage id="bagTable.bagId" defaultMessage="Bag ID"/>
                                            </TableCell>
                                            < TableCell alignmentHorizontal="center" sortColumn="eventType">
                                                <FormattedMessage id="bagTable.bagStatus" defaultMessage="Bag Status"/>
                                            </TableCell>
                                            <TableCell alignmentHorizontal="center" sortColumn="departEventTime">
                                                <FormattedMessage id="bagTable.departTime"
                                                                  defaultMessage="Depart Time"/>
                                            </TableCell>
                                            <TableCell alignmentHorizontal="center" sortColumn="receiveEventTime">
                                                <FormattedMessage id="bagTable.receiveTime"
                                                                  defaultMessage="Receive Time"/>
                                            </TableCell>
                                        </TableRow>

                                        <TableRow>
                                            <TableCell>
                                                <Input alignmentHorizontal="center" type="text" size="small"
                                                       value={transporterIdFilter}
                                                       onChange={setTransporterIdFilter}/>
                                            </TableCell>
                                            <TableCell>
                                                <Input alignmentHorizontal="center" type="text" size="small"
                                                       value={bagIdFilter}
                                                       onChange={setBagIdFilter}/>
                                            </TableCell>
                                            <TableCell>
                                                <Input type="text" size="small" value={bagStatusFilter}
                                                       onChange={setBagStatusFilter}/>
                                            </TableCell>
                                        </TableRow>
                                        {
                                            bagInfoList ?
                                                bagInfoList.map((bag, key) => (
                                                    (key >= startIndexInPage && key <= endIndexInPage) ?
                                                        <TableRow key={bag.bagId}>
                                                            <TableCell
                                                                alignmentHorizontal="center">{bag.transporterId}</TableCell>
                                                            <TableCell
                                                                alignmentHorizontal="center">{bag.bagId}</TableCell>
                                                            <TableCell
                                                                alignmentHorizontal="center">{bag.eventType}</TableCell>
                                                            <TableCell
                                                                alignmentHorizontal="center">{convertTimestampToLocaleString(bag.departEventTime, bag.eventType, BAG_TRACKING_CONSTANTS.DEPART_TIME)}</TableCell>
                                                            <TableCell
                                                                alignmentHorizontal="center">{convertTimestampToLocaleString(bag.receiveEventTime, bag.eventType, BAG_TRACKING_CONSTANTS.RECEIVE_TIME)}</TableCell>
                                                        </TableRow> : null
                                                )) : null
                                        }
                                    </Table>
                                </div>
                                <div>
                                    <Row width="100%" alignmentHorizontal="center" spacingInset="medium none none">
                                        <Pagination
                                            numberOfPages={totalPages}
                                            onChange={setCurrentPage}
                                            currentPage={currentPage}/>
                                    </Row>
                                </div>
                            </div>
                        </Row>
                    </ScrollableElementWrapper>
                </div>
            </div>
    )
}

export default BagTracking


// WEBPACK FOOTER //
// ./src/components/devicemanagement/bagtracking/BagTracking.js